/* eslint-disable import/prefer-default-export */
/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
/**
 * Assignments Bridge Format Adapter
 * Adapter to transform assignments data to new API format and vice versa
 * @file AssignmentsBridgeFormatAdapter.js
 * @author Satyaki Bose
 */

import {
  ASSIGNMENT_CRUD_FIELDS,
  ASSIGNMENT_STATUS_TYPES,
  ASSIGNMENT_TYPES,
  USE_ASSIGNMENT_PLAY_ORDER_SORT
} from '../common/Constants';
import CommonUtils from '../../../common/utils/CommonUtils';
import { sortContentByPlayOrder } from './AssignmentUtils';

const getUniqueIds = (contentCovered, fieldName) => {
  if (!contentCovered || !Array.isArray(contentCovered)) return [];

  return [...new Set(
    contentCovered
      .map(content => content[fieldName])
      .filter(Boolean)
  )];
};

/**
 * Transform contentCovered to API items format
 */
export const toApiItems = (contentCovered) => {
  if (!contentCovered || !Array.isArray(contentCovered)) return [];

  return contentCovered.flatMap(content => (content.questions || []).map(question => ({
    ...(question.itemId ? { itemId: question.itemId } : {}),
    activity: {
      activityId: question.questionId,
      activityType: question.activityType || ASSIGNMENT_CRUD_FIELDS.ACTIVITY_TYPE_IPX
    },
    tags: {
      chapterId: content.chapterId,
      objectiveId: content.pageId,
      chapterNumber: content.chapterNumber,
      chapterTitle: content.chapterTitle,
      sectionNumber: content.sectionNumber,
      sectionTitle: content.sectionTitle
    }
  })));
};

/**
 * Transform assignment to API format
 */
export const constructAssignmentPayload = (assignment, contextId) => {
  const sortedContentCovered = USE_ASSIGNMENT_PLAY_ORDER_SORT
    ? sortContentByPlayOrder(assignment?.contentCovered)
    : assignment?.contentCovered;
  // Build readOnlyContents for pages without questions
  const readOnlyContents = sortedContentCovered
    .filter(content => !content.questions || content.questions.length === 0)
    .map(content => ({
      chapterId: content.chapterId,
      objectiveId: content.pageId,
      chapterNumber: content.chapterNumber,
      chapterTitle: content.chapterTitle,
      sectionNumber: content.sectionNumber,
      sectionTitle: content.sectionTitle
    }));

  const tags = assignment.tags || {
    chapterIds: JSON.stringify(getUniqueIds(sortedContentCovered, 'chapterId')),
    bookId: assignment.bookId,
    pageIds: JSON.stringify(getUniqueIds(sortedContentCovered, 'pageId')),
    readOnlyContents: JSON.stringify(readOnlyContents)
  };

  if (assignment.assignmentStatus === ASSIGNMENT_STATUS_TYPES.ASSIGNABLE) {
    let assignableMetaData = assignment.assignableMetaData;

    if (!assignableMetaData) {
      const meta = {
        ...(assignment.dateNormalizationAction && {
          dateNormalizationAction: assignment.dateNormalizationAction
        }),
        ...(assignment.coverageDedupeAction && {
          coverageDedupeAction: assignment.coverageDedupeAction
        }),
        ...(assignment.validationErrors?.length && {
          validationErrors: assignment.validationErrors
            .map((error) => (error?.type ? { type: error.type } : null))
            .filter(Boolean)
        })
      };

      if (Object.keys(meta).length > 0) {
        assignableMetaData = JSON.stringify(meta);
      }
    }

    if (assignableMetaData) {
      tags.assignableMetaData = assignableMetaData;
    }
  }

  return {
    title: assignment.title,
    assignmentStatus: assignment.assignmentStatus,
    dateAvailable: assignment.dateAvailable,
    ...(assignment.dueDate && { dateDue: assignment.dueDate }),
    originalAssignmentId: assignment.originalAssignmentId,
    learningContextId: contextId,
    createdBy: assignment.createdBy,
    ...(assignment.assignmentDeepLinkId && { assignmentDeepLinkId: assignment.assignmentDeepLinkId }),
    ...(assignment.historyId && { historyId: assignment.historyId }),
    tags,
    learningResources: [{
      type: ASSIGNMENT_CRUD_FIELDS.ASSESSMENT,
      assessment: {
        assessmentId: assignment.assessmentId,
        assessmentType: ASSIGNMENT_TYPES.HOMEWORK,
        assessmentFormat: ASSIGNMENT_CRUD_FIELDS.MEDIA_AND_QUESTION,
        items: toApiItems(sortedContentCovered)
      }
    }]
  };
};

/**
 * Transform API response to internal format
 */
export const extractAssignments = (assignment) => {
  const assessment = assignment.assessment;
  if (!assessment) {
    return {
      ...assignment,
      contentCovered: [],
      assessmentId: null,
      originalAssignmentId: assignment.originalAssignmentId ?? null,
      assignableMetaData: assignment.tags?.assignableMetaData ?? null
    };
  }

  const readOnlyContents = CommonUtils.safeParseJsonArray(assignment.tags?.readOnlyContents);

  const itemsByPageId = new Map();

  if (assessment.items && Array.isArray(assessment.items)) {
    assessment.items.forEach((item) => {
      const pageId = item.tags?.objectiveId;
      if (pageId) {
        if (!itemsByPageId.has(pageId)) {
          itemsByPageId.set(pageId, []);
        }
        itemsByPageId.get(pageId).push(item);
      }
    });
  }

  const contentCovered = [];

  // 1. Add pages WITH questions from items
  assessment.items?.forEach((assessmentItem) => {
    const tags = assessmentItem.tags || {};
    const pageId = tags.objectiveId;

    // Check if already added
    if (contentCovered.find(c => c.pageId === pageId)) return;

    const itemsForPage = itemsByPageId.get(pageId) || [];

    contentCovered.push({
      chapterId: tags.chapterId,
      chapterTitle: tags.chapterTitle,
      chapterNumber: tags.chapterNumber,
      pageId,
      sectionNumber: tags.sectionNumber,
      sectionTitle: tags.sectionTitle,
      questions: itemsForPage.map(item => ({
        questionId: item.activity.activityId,
        activityType: item.activity.activityType || ASSIGNMENT_CRUD_FIELDS.ACTIVITY_TYPE_IPX,
        point: item.points || 1,
        itemId: item.itemId || '',
        scoringMode: item.scoringMode || ASSIGNMENT_CRUD_FIELDS.SCORING_MODE_NORMAL
      }))
    });
  });

  // 2. Add pages WITHOUT questions from readOnlyContents
  readOnlyContents.forEach((section) => {
    contentCovered.push({
      chapterId: section.chapterId,
      chapterTitle: section.chapterTitle,
      chapterNumber: section.chapterNumber,
      pageId: section.objectiveId,
      sectionNumber: section.sectionNumber,
      sectionTitle: section.sectionTitle,
      questions: []
    });
  });

  return {
    ...assignment,
    contentCovered: USE_ASSIGNMENT_PLAY_ORDER_SORT ? sortContentByPlayOrder(contentCovered) : contentCovered,
    assessmentId: assessment.assessmentId,
    id: assignment.assignmentId || assignment.id || '',
    bookId: assignment.tags?.bookId || '',
    assignmentType: assessment?.assessmentType || ASSIGNMENT_TYPES.HOMEWORK,
    dateAvailable: assignment.dateAvailable || '',
    dueDate: assignment.dateDue || '',
    originalAssignmentId: assignment.originalAssignmentId ?? null,
    assignableMetaData: assignment.tags?.assignableMetaData ?? null
  };
};
